<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();

// Computed properties
const humanEscalation = computed(
  () => store.state.assistantWizard.humanEscalation
);
const humanEscalationConfig = computed(
  () => store.state.assistantWizard.humanEscalationConfig
);
const humanEscalationOptions = computed(
  () => store.state.assistantWizard.humanEscalationOptions
);
const name = computed(() => store.state.assistantWizard.name);
const error = computed(() => store.state.assistantWizard.error);
const teams = computed(() => store.getters['teams/getTeams']);

const hasAvailableTeams = computed(() => {
  return teams.value.some(team => team.allow_auto_assign);
});

const filteredTeams = computed(() => {
  return teams.value.filter(team => team.allow_auto_assign);
});

const validationError = computed(() => error.value === 'human_escalation');
const selectedTeamId = computed(() => {
  const configuredTeamId = humanEscalationConfig.value?.value;
  if (filteredTeams.value.some(team => team.id === configuredTeamId)) {
    return configuredTeamId;
  }
  return filteredTeams.value[0]?.id || '';
});

// Methods
const next = () => store.dispatch('assistantWizard/next');

const changeHumanEscalation = e => {
  let newHumanEscalation = e.currentTarget.id;
  if (newHumanEscalation === humanEscalation.value) {
    newHumanEscalation = null;
    store.dispatch('assistantWizard/updateHumanEscalation', newHumanEscalation);
    store.dispatch('assistantWizard/updateHumanEscalationConfig', {});
    store.dispatch('assistantWizard/validateState');
    return;
  }
  store.dispatch('assistantWizard/updateHumanEscalation', newHumanEscalation);
};

const changeHumanEscalationConfig = e => {
  const value = e.target.value;
  const configItem = humanEscalationOptions.value.find(
    ({ key }) => key === humanEscalation.value
  ).configurationKey;
  store.dispatch('assistantWizard/updateHumanEscalationConfig', {
    item: configItem,
    value,
  });
};

const defaultConfig = () => {
  if (humanEscalation.value === 'assign_team' && teams.value?.length > 0) {
    const firstAllowedTeam = teams.value.find(team => team.allow_auto_assign);
    return firstAllowedTeam?.id || '';
  }
  return '';
};

const ensureDefaultTeamSelection = () => {
  if (humanEscalation.value !== 'assign_team' || !hasAvailableTeams.value) {
    return;
  }
  const option = humanEscalationOptions.value.find(
    ({ key }) => key === humanEscalation.value
  );
  if (!option || selectedTeamId.value === humanEscalationConfig.value?.value) {
    return;
  }

  store.dispatch('assistantWizard/updateHumanEscalationConfig', {
    item: option.configurationKey,
    value: selectedTeamId.value,
  });
};

const refreshTeams = async () => {
  if (document.visibilityState === 'hidden') {
    return;
  }
  await store.dispatch('teams/get');
  ensureDefaultTeamSelection();
};

// Watchers
watch(humanEscalation, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    const option = humanEscalationOptions.value.find(
      ({ key }) => key === newVal
    );
    if (option) {
      store.dispatch('assistantWizard/updateHumanEscalationConfig', {
        item: option.configurationKey,
        value: defaultConfig(),
      });
    } else {
      store.dispatch('assistantWizard/updateHumanEscalationConfig', {});
    }
  }
});

watch([humanEscalation, filteredTeams], () => {
  ensureDefaultTeamSelection();
});

// Lifecycle hooks
onMounted(async () => {
  await refreshTeams();
  window.addEventListener('focus', refreshTeams);
  document.addEventListener('visibilitychange', refreshTeams);
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshTeams);
  document.removeEventListener('visibilitychange', refreshTeams);
});
</script>

<template>
  <section class="flex items-center flex-col w-full max-w-[800px]">
    <form
      class="flex flex-col w-full flex-1 mb-4 items-center"
      @submit.prevent="next"
    >
      <label
        class="text-xl text-center text-neutral-900 dark:text-neutral-50 mb-8"
        for="humanEscalation"
      >
        {{ $t('ASSISTANT_MGMT.WIZARD.HUMANESCALATION_LABEL', { name: name }) }}
      </label>
      <div class="flex flex-col md:flex-row gap-4 w-full">
        <div
          v-for="option in humanEscalationOptions"
          :id="option.key"
          :key="option.key"
          :value="humanEscalationConfig.value"
          class="box flex flex-col items-center w-full p-6 border border-2 rounded-xl text-center cursor-pointer bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-woot-600 bg-white"
          :class="{ active: humanEscalation === option.key }"
          @click="changeHumanEscalation"
        >
          <div
            class="check-container bg-neutral-100 dark:bg-neutral-800 rounded-full text-white mb-2 p-2"
          >
            <fluent-icon v-if="option.icon" :icon="option.icon" />
          </div>
          <h3 class="mb-2 font-semibold text-lg">{{ option.title }}</h3>
          <p class="leading-tight">{{ option.description }}</p>
        </div>
      </div>

      <div
        v-if="humanEscalation === 'assign_team'"
        class="flex flex-col gap-4 items-center py-8 w-full"
      >
        <template v-if="hasAvailableTeams">
          {{ name }} will pass the conversation to the Rookoo help desk for one
          of your human co-workers to resolve.
          <select
            id="team"
            :value="selectedTeamId"
            class="rounded-md border-2 border-neutral-100 text-center w-80 h-10 leading-5 w-full"
            @change="changeHumanEscalationConfig"
          >
            <option
              v-for="team in filteredTeams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
        </template>
        <template v-else>
          <div class="text-center">
            <p class="mb-4">
              You need to create a team first to enable human escalation.
            </p>
            <router-link :to="{ name: 'settings_teams_new' }" target="_blank">
              <NextButton
                type="button"
                md
                slate
                icon="i-lucide-circle-plus"
                :label="$t('Create team')"
              />
            </router-link>
          </div>
        </template>
      </div>

      <div
        v-if="humanEscalation === 'close_conversation'"
        class="flex flex-col gap-4 items-center py-8 w-full"
      >
        Tell {{ name }} what to do before closing the conversation.
        <textarea
          id="instructions"
          :value="humanEscalationConfig.value"
          @input="changeHumanEscalationConfig"
        />
      </div>

      <span v-if="validationError" class="text-red-600">
        Please fill in all fields
      </span>
    </form>
  </section>
</template>

<style scoped lang="scss">
.box.active {
  border-color: #645298;
  background: #f0eef6;
}
.dark .box.active {
  border-color: #645298;
  background: #292932;
}
.box.active p {
  font-weight: 600;
}
.box.active .check-container {
  background: #645298;
}
</style>
